import guidesFile from '../config/release-guides.json'

export const RELEASE_GUIDES_FLAG = 'FEON-63-in-app-guides-backfill'
export const CHATGPT_PLUGIN_GUIDE_ID = 'integrations-chatgpt-plugin'

// Stored on the user's personalInformation so seen/clicked state follows the account, not the browser.
export const RELEASE_GUIDES_STATE_FIELD = 'releaseGuides'

export const GUIDE_KIND = Object.freeze({ DOT: 'dot', BADGE: 'badge' })

// A guide with an `audience` is gated by that check on top of the shared hosting-history gate.
export const GUIDE_AUDIENCE = Object.freeze({ NO_COURSES: 'no-courses' })

export const MAX_MARKERS_PER_VIEW = Object.freeze({ [GUIDE_KIND.DOT]: 2, [GUIDE_KIND.BADGE]: 3 })

const DAY_MS = 24 * 60 * 60 * 1000
export const MARKER_LIFETIME_MS = Object.freeze({ [GUIDE_KIND.DOT]: 14 * DAY_MS, [GUIDE_KIND.BADGE]: 28 * DAY_MS })
export const MAX_UNCLICKED_DOT_VIEWS = 3

export const GUIDE_VIEW = Object.freeze({
  HOME: 'home',
  EDITOR: 'editor',
  SLIDE_PICKER: 'slide-picker',
  TEAM_SETUP: 'team-setup',
  INTEGRATIONS: 'integrations',
  MASTER_AGENT_PANEL: 'master-agent-panel',
})

// Markers this module does not own that can already sit on a view; they use up its slots first.
export const EXISTING_MARKERS_PER_VIEW = Object.freeze({
  [GUIDE_VIEW.HOME]: { [GUIDE_KIND.DOT]: 0, [GUIDE_KIND.BADGE]: 1 },
  [GUIDE_VIEW.EDITOR]: { [GUIDE_KIND.DOT]: 1, [GUIDE_KIND.BADGE]: 1 },
  [GUIDE_VIEW.SLIDE_PICKER]: { [GUIDE_KIND.DOT]: 0, [GUIDE_KIND.BADGE]: 0 },
  [GUIDE_VIEW.TEAM_SETUP]: { [GUIDE_KIND.DOT]: 0, [GUIDE_KIND.BADGE]: 0 },
  [GUIDE_VIEW.INTEGRATIONS]: { [GUIDE_KIND.DOT]: 0, [GUIDE_KIND.BADGE]: 0 },
  [GUIDE_VIEW.MASTER_AGENT_PANEL]: { [GUIDE_KIND.DOT]: 0, [GUIDE_KIND.BADGE]: 0 },
})

/**
 * One entry per passive release guide, one per line in `config/release-guides.json`. An optional `flag` holds a guide
 * behind its own toggle on top of the umbrella one. `views` lists every view the anchor can appear on; `slideTypes`
 * ties a slide-picker guide to the picker items it badges, keyed as `pickerGuideKey` reads them. `labelKey` gives a
 * dot a hover label, shown while the pointer or focus is on its anchor. `area` is the label analytics report it under.
 */
export const RELEASE_GUIDES = Object.freeze(guidesFile.guides)

const GUIDES_BY_ID = new Map(RELEASE_GUIDES.map((guide) => [guide.id, guide]))

export const findGuide = (guideId) => GUIDES_BY_ID.get(guideId) || null

const OFFICIAL_PUBLISHED_KEYS = Object.freeze(new Set([
  // production
  '5c6c6d56-4f5e-4795-aa4a-ca1d479727dc', // escape-room-v2
  '29cbd1b6-81f8-401e-b474-e67aa3c9861d', // two-by-two-grid-v2
  '95d571e5-a777-4cde-b5ab-c05620613ad2', // image-show
  'b1e5154d-0cd7-49a4-9631-18ff1622cc7d', // draw-answer-v2
  'b0bb1c51-7fc7-4149-a5bc-5c47c4cb8191', // true-or-false-gen4
  '05e0e63b-d949-4d45-a3a5-deafedede858', // fill-in-the-blanks
  'c57c7cb7-aaa2-4508-9430-2584c1026df9', // calm
  'f693e7b2-8d8d-4821-a2e9-324f7f94e937', // split-the-points
  '25cbd405-9a66-41b8-ba97-98db0a1c4c25', // pin-on-image-quiz
  '516869d4-061b-49c7-831c-c11e8c7d8d44', // personality-test
  // staging
  'e585094c-f10c-4db0-9654-4fcbf433ec14', // escape-room-v2
  '3847dacf-df2d-46a1-98b2-9b8280b47256', // two-by-two-grid-v2
  '49e02874-fd8f-4a71-a6dd-1f0407c27883', // image-show
  'c898bcf6-e1a4-4622-9e2c-aba4efac486e', // draw-answer-v2
  '94cf5523-9174-4225-89bf-18fe92e1a873', // true-or-false-gen4
  '1e82a0ca-7421-43de-8049-15b8451d0bf0', // fill-in-the-blanks
  'af929ed0-5fdc-4251-8201-e84d078ee62d', // calm
  'e537d9d4-390c-4c35-9ead-53078dcd635d', // split-the-points
  '4b37214a-264a-4edc-8a5a-7a1648d6da12', // pin-on-image-quiz
  'df6c2cde-d0a0-464f-94ea-afa833e03c85', // personality-test
]))

/**
 * The key a picker item is matched on: its catalogue `type`, or `published/<slug>` for a developer-published
 * slide type, whose `type` is a snapshot page id that differs per environment. Only AhaSlides' own published
 * types (vetted `publishedKey`s) are matched on slug, because any creator can publish a type with any slug.
 */
export const pickerGuideKey = (item) => (
  item && item.isPublishedSlideType && item.slug && OFFICIAL_PUBLISHED_KEYS.has(item.publishedKey)
    ? `published/${item.slug}`
    : item?.type
)

export const findPickerGuideForSlideType = (slideType, guides = RELEASE_GUIDES) => (
  (slideType && guides.find((guide) => (guide.slideTypes || []).includes(slideType))) || null
)

export const pickerGuides = (guides = RELEASE_GUIDES) => (
  guides.filter((guide) => guide.views.includes(GUIDE_VIEW.SLIDE_PICKER))
)

const toTime = (value) => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? null : time
}

export const readGuideStore = (personalInformation) => {
  const stored = personalInformation?.[RELEASE_GUIDES_STATE_FIELD]
  const store = stored && typeof stored === 'object' ? stored : {}
  const guides = store.guides && typeof store.guides === 'object' ? store.guides : {}
  return { eligibleAt: store.eligibleAt || null, guides }
}

export const readGuideState = (guideStore, guideId) => {
  const stored = guideStore.guides[guideId] || {}
  return {
    firstSeenAt: stored.firstSeenAt || null,
    clickedAt: stored.clickedAt || null,
    timedOutAt: stored.timedOutAt || null,
    views: Number.isFinite(stored.views) ? stored.views : 0,
  }
}

/** True once the marker's own clock or view count has run out, whether or not it was ever clicked. */
export const hasGuideTimedOut = (guide, guideState, now = Date.now()) => {
  if (guide.kind === GUIDE_KIND.DOT && guideState.views >= MAX_UNCLICKED_DOT_VIEWS) return true
  const firstSeenTime = toTime(guideState.firstSeenAt)
  return firstSeenTime !== null && now - firstSeenTime >= MARKER_LIFETIME_MS[guide.kind]
}

export const isGuideFinished = (guide, guideState, now = Date.now()) => (
  !!guideState.clickedAt || !!guideState.timedOutAt || hasGuideTimedOut(guide, guideState, now)
)

/** A guide still worth a slot: not clicked and not timed out. */
export const isGuideActive = (guideStore, guide, now = Date.now()) => (
  !isGuideFinished(guide, readGuideState(guideStore, guide.id), now)
)

const writeGuideState = (guideStore, guideId, guideState) => ({
  ...guideStore,
  guides: { ...guideStore.guides, [guideId]: guideState },
})

const stampTime = (now) => new Date(now).toISOString()

export const recordEligible = (guideStore, now = Date.now()) => ({
  ...guideStore,
  eligibleAt: guideStore.eligibleAt || stampTime(now),
})

/** One view in which the marker was actually shown; dots count these towards their view limit. */
export const recordGuideShown = (guideStore, guideId, now = Date.now()) => {
  const state = readGuideState(guideStore, guideId)
  return writeGuideState(guideStore, guideId, {
    ...state,
    firstSeenAt: state.firstSeenAt || stampTime(now),
    views: state.views + 1,
  })
}

export const recordGuideClicked = (guideStore, guideId, now = Date.now()) => {
  const state = readGuideState(guideStore, guideId)
  return writeGuideState(guideStore, guideId, { ...state, clickedAt: state.clickedAt || stampTime(now) })
}

export const recordGuideTimedOut = (guideStore, guideId, now = Date.now()) => {
  const state = readGuideState(guideStore, guideId)
  return writeGuideState(guideStore, guideId, { ...state, timedOutAt: state.timedOutAt || stampTime(now) })
}

/** A guide whose clock ran out but whose "Timed out" event has not been recorded yet. */
export const isTimeoutUnrecorded = (guide, guideState, now = Date.now()) => (
  !!guideState.firstSeenAt && !guideState.clickedAt && !guideState.timedOutAt
  && hasGuideTimedOut(guide, guideState, now)
)

const toLaunchTime = (guide) => toTime(guide.launchedOn) || 0

/** Lower tier first (T1 is the most important), then the newest launch. */
export const compareGuidePriority = (left, right) => (
  left.tier - right.tier || toLaunchTime(right) - toLaunchTime(left) || left.id.localeCompare(right.id)
)

/**
 * Which of the guides waiting on a view get its free slots. The rest stay queued and take a slot
 * once one frees; nothing is dropped.
 */
export const allocateViewSlots = (waitingGuides, view) => {
  const existing = EXISTING_MARKERS_PER_VIEW[view] || {}
  const freeSlots = (kind) => Math.max(0, MAX_MARKERS_PER_VIEW[kind] - (existing[kind] || 0))
  const allocated = new Set()
  Object.values(GUIDE_KIND).forEach((kind) => {
    waitingGuides
      .filter((guide) => guide.kind === kind)
      .sort(compareGuidePriority)
      .slice(0, freeSlots(kind))
      .forEach((guide) => allocated.add(guide.id))
  })
  return allocated
}

/**
 * Picker items with their "New" pill decided by the release guides alone: at most the picker's badge
 * cap, highest priority first, among live guides whose slide type is actually in the list. A badged
 * item carries `releaseGuideId`; every other item's own `isNew` is cleared.
 */
export const withPickerGuideBadges = (slideTypes, liveGuides) => {
  const presentTypes = new Set(slideTypes.map(pickerGuideKey))
  const guidesInList = liveGuides.filter((guide) => (guide.slideTypes || []).some((type) => presentTypes.has(type)))
  const badgedGuideIds = allocateViewSlots(guidesInList, GUIDE_VIEW.SLIDE_PICKER)
  const badgedGuides = guidesInList.filter((guide) => badgedGuideIds.has(guide.id))
  return slideTypes.map((item) => {
    const guide = findPickerGuideForSlideType(pickerGuideKey(item), badgedGuides)
    return guide ? { ...item, isNew: true, releaseGuideId: guide.id } : { ...item, isNew: false }
  })
}

export const withGuideItemsFirst = (pool) => [
  ...pool.filter((item) => item.releaseGuideId),
  ...pool.filter((item) => !item.releaseGuideId),
]

const itemIdentity = (item) => `${item.type}::${item.slideType || ''}`

export const withBucketGuideItemsFirst = (items, bucket) => {
  const guided = (bucket || []).filter((item) => item.releaseGuideId)
  if (!guided.length) return items
  const guidedIdentities = new Set(guided.map(itemIdentity))
  const rest = items.filter((item) => !guidedIdentities.has(itemIdentity(item)))
  return [...guided, ...rest].slice(0, items.length)
}
